/**
 * Use this file for JavaScript code that you want to run in the front-end
 * on posts/pages that contain this block.
 *
 * When this file is defined as the value of the `viewScript` property
 * in `block.json` it will be enqueued on the front end of the site.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-metadata/#view-script
 */

/**
 * Abre un item del acordeón
 * @param {HTMLElement} accordionItem - El item a abrir
 * @param {string} expandedBgColor - Color de fondo cuando está expandido
 */
function openItem(accordionItem, expandedBgColor) {
	const itemContent = accordionItem.querySelector(".sc-info-accordion__item-content");
	const itemHeader = accordionItem.querySelector(".sc-info-accordion__item-header");

	accordionItem.classList.add("sc-info-accordion__item--open");
	accordionItem.style.backgroundColor = expandedBgColor;
	itemHeader.setAttribute("aria-expanded", "true");
	itemContent.setAttribute("aria-hidden", "false");

	const contentHeight = itemContent.scrollHeight;
	itemContent.style.maxHeight = contentHeight + "px";
}

/**
 * Cierra un item del acordeón
 * @param {HTMLElement} accordionItem - El item a cerrar
 * @param {string} collapsedBgColor - Color de fondo cuando está colapsado
 */
function closeItem(accordionItem, collapsedBgColor) {
	const itemContent = accordionItem.querySelector(".sc-info-accordion__item-content");
	const itemHeader = accordionItem.querySelector(".sc-info-accordion__item-header");

	accordionItem.classList.remove("sc-info-accordion__item--open");
	accordionItem.style.backgroundColor = collapsedBgColor;
	itemHeader.setAttribute("aria-expanded", "false");
	itemContent.setAttribute("aria-hidden", "true");

	itemContent.style.maxHeight = "0";
}

/**
 * Cierra todos los items excepto el especificado
 * @param {NodeList} items - Todos los items del acordeón
 * @param {HTMLElement} currentItem - El item a mantener abierto
 * @param {string} collapsedBgColor - Color de fondo cuando está colapsado
 */
function closeOtherItems(items, currentItem, collapsedBgColor) {
	items.forEach((otherItem) => {
		if (otherItem !== currentItem) {
			closeItem(otherItem, collapsedBgColor);
		}
	});
}

/**
 * Maneja el click en el header del acordeón
 * @param {HTMLElement} item - El item del acordeón
 * @param {NodeList} items - Todos los items del acordeón
 * @param {boolean} allowMultiple - Si permite múltiples items abiertos
 * @param {string} collapsedBgColor - Color de fondo cuando está colapsado
 * @param {string} expandedBgColor - Color de fondo cuando está expandido
 */
function handleHeaderClick(item, items, allowMultiple, collapsedBgColor, expandedBgColor) {
	const isOpen = item.classList.contains("sc-info-accordion__item--open");

	if (!allowMultiple && !isOpen) {
		closeOtherItems(items, item, collapsedBgColor);
	}

	if (isOpen) {
		closeItem(item, collapsedBgColor);
	} else {
		openItem(item, expandedBgColor);
	}
}

/**
 * Maneja el evento keydown en el header del acordeón
 * @param {KeyboardEvent} e - El evento de teclado
 * @param {HTMLElement} header - El elemento header
 */
function handleHeaderKeydown(e, header) {
	if (e.key === "Enter" || e.key === " ") {
		e.preventDefault();
		header.click();
	}
}

/**
 * Configura los event listeners para un item del acordeón
 * @param {HTMLElement} item - El item del acordeón
 * @param {NodeList} items - Todos los items del acordeón
 * @param {boolean} allowMultiple - Si permite múltiples items abiertos
 * @param {string} collapsedBgColor - Color de fondo cuando está colapsado
 * @param {string} expandedBgColor - Color de fondo cuando está expandido
 */
function setupAccordionItem(item, items, allowMultiple, collapsedBgColor, expandedBgColor) {
	const header = item.querySelector(".sc-info-accordion__item-header");
	const content = item.querySelector(".sc-info-accordion__item-content");

	if (!header || !content) {
		return;
	}

	header.addEventListener("click", (e) => {
		e.preventDefault();
		handleHeaderClick(item, items, allowMultiple, collapsedBgColor, expandedBgColor);
	});

	header.addEventListener("keydown", (e) => {
		handleHeaderKeydown(e, header);
	});
}

/**
 * Inicializa un componente acordeón
 * @param {HTMLElement} accordion - El contenedor del acordeón
 */
function initAccordion(accordion) {
	const allowMultiple = accordion.dataset.allowMultiple === "true";
	const collapsedBgColor = accordion.dataset.collapsedBg;
	const expandedBgColor = accordion.dataset.expandedBg;
	const items = accordion.querySelectorAll(".sc-info-accordion__item");

	items.forEach((item) => {
		setupAccordionItem(item, items, allowMultiple, collapsedBgColor, expandedBgColor);
	});
}

/**
 * Maneja la navegación por hash en la URL para abrir items específicos
 */
function handleHashNavigation() {
	const hash = globalThis.location.hash;
	if (!hash) {
		return;
	}

	const targetItem = document.querySelector(hash);
	if (!targetItem?.classList.contains("sc-info-accordion__item")) {
		return;
	}

	const header = targetItem.querySelector(".sc-info-accordion__item-header");
	if (!header) {
		return;
	}

	setTimeout(() => {
		header.click();
		targetItem.scrollIntoView({ behavior: "smooth", block: "center" });
	}, 100);
}

document.addEventListener("DOMContentLoaded", () => {
	const accordions = document.querySelectorAll(".sc-info-accordion");
	accordions.forEach(initAccordion);
	handleHashNavigation();
});
